import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileArrowUp, FilePdf, FileImage, File as FileIcon, Trash, DownloadSimple } from "@phosphor-icons/react";

const DOC_TYPES = ["Aadhaar / Identity", "PAN", "Address Proof", "Photograph", "Agreement", "Certificate", "Other"];
const isOtherType = (v) => ["other", "others"].includes(String(v ?? "").toLowerCase());

export default function DocumentManager({ open, onOpenChange, entity, entityId, entityName }) {
  const { can } = useAuth();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("");
  const [otherMode, setOtherMode] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/documents", { params: { entity, entity_id: entityId } });
      setDocs(res.data);
    } catch { setDocs([]); }
  }, [entity, entityId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity", entity);
    fd.append("entity_id", entityId);
    fd.append("doc_type", docType || "Other");
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const iconFor = (ct) => {
    if (ct?.includes("pdf")) return <FilePdf size={20} weight="duotone" className="text-red-500" />;
    if (ct?.includes("image")) return <FileImage size={20} weight="duotone" className="text-blue-500" />;
    return <FileIcon size={20} weight="duotone" className="text-zinc-500" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Documents · {entityName}</DialogTitle>
        </DialogHeader>

        {can("documents.add") && (
          <div className="flex items-end gap-2 border border-zinc-200 rounded-sm p-3 bg-zinc-50">
            <div className="flex-1">
              <Label className="text-xs text-zinc-500 mb-1.5 block">Document type</Label>
              <Select value={otherMode ? "Other" : docType} onValueChange={(v) => { if (isOtherType(v)) { setOtherMode(true); setDocType(""); } else { setOtherMode(false); setDocType(v); } }}>
                <SelectTrigger data-testid="doc-type-select" className="rounded-sm border-zinc-200 h-9 bg-white"><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent className="rounded-sm">
                  {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {otherMode && <Input data-testid="doc-type-custom" value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="Enter document type" className="rounded-sm border-zinc-200 h-9 bg-white mt-2" />}
            </div>
            <label data-testid="doc-upload-label" className="cursor-pointer inline-flex items-center gap-2 h-9 px-4 bg-zinc-900 text-white text-sm rounded-sm hover:bg-zinc-800 transition-colors">
              <FileArrowUp size={16} /> {uploading ? "Uploading…" : "Upload"}
              <input data-testid="doc-upload-input" type="file" className="hidden" onChange={onUpload} disabled={uploading} />
            </label>
          </div>
        )}

        <div className="space-y-2 max-h-72 overflow-y-auto mt-2">
          {docs.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-8">No documents uploaded.</p>
          ) : (
            docs.map((d) => (
              <div key={d.id} data-testid="doc-row" className="flex items-center gap-3 border border-zinc-200 rounded-sm px-3 py-2">
                {iconFor(d.content_type)}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-800 truncate">{d.original_filename}</div>
                  <div className="text-[11px] text-zinc-400">{d.doc_type} · {fmtDateTime(d.created_at)}</div>
                </div>
                <a
                  href={`${api.defaults.baseURL}/documents/${d.id}/download`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-400 hover:text-zinc-900"
                  data-testid="doc-download-link"
                >
                  <DownloadSimple size={16} />
                </a>
                {can("documents.delete") && (
                  <button onClick={() => del(d.id)} className="text-zinc-400 hover:text-red-600" data-testid="doc-delete-button">
                    <Trash size={16} />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
